import React, { useEffect, useState } from 'react';

import Button, { type ButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type { User as UserPickerUser } from '@atlaskit/user-picker';

const ENTRY_DURATION = 0.36;
const EXIT_DURATION = 0.12;
const MAX_SUGGESTED_USERS = 2;

const styles = cssMap({
	container: {
		marginTop: token('space.025'),
		width: '100%',
		position: 'relative',
	},
	entry: {
		flex: '0 1 auto',
		maxWidth: '42%',
		opacity: 0,
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		transition: `opacity ${ENTRY_DURATION}s ease-in`,
	},
	base: {
		flex: '0 1 auto',
		maxWidth: '42%',
		opacity: 1,
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		overflow: 'hidden',
		transition: `opacity ${ENTRY_DURATION}s ease-in`,
	},
	exit: {
		flex: '0 1 auto',
		maxWidth: '0%',
		opacity: 0,
		overflow: 'hidden',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		transition: `all ${EXIT_DURATION}s ease-out`,
	},
});

interface SuggestedMemberPillProps {
	user: UserPickerUser;
	onClick: (user: UserPickerUser) => void;
}

const SuggestedMemberPill = ({ user, onClick }: SuggestedMemberPillProps) => {
	const [animationState, setAnimationState] = useState('entry');

	const handleUserPillClick: ButtonProps['onClick'] = () => {
		setAnimationState('exit');
		setTimeout(() => {
			onClick(user);
		}, EXIT_DURATION * 1000);
	};

	useEffect(() => {
		setTimeout(() => setAnimationState('base'));
	}, []);

	return (
		<Box
			xcss={
				animationState === 'entry'
					? styles.entry
					: animationState === 'exit'
						? styles.exit
						: styles.base
			}
		>
			<Button
				spacing="compact"
				appearance="default"
				iconBefore={AddIcon}
				onClick={handleUserPillClick}
			>
				{user.name}
			</Button>
		</Box>
	);
};

interface SuggestedTeamMembersProps {
	currentPickedUsers: UserPickerUser[];
	onSuggestedUserClick: (selectedUser: UserPickerUser) => void;
	initialValues?: UserPickerUser[];
}

const mockData = [
	{
		entityType: 'USER',
		id: '712020:c58ee83b-0f59-4d12-8adc-047cc1d5354c',
		name: 'Charlie Sans',
		avatarUrl:
			'https://secure.gravatar.com/avatar/818be9ace60cae74d3409df8c341f9ea?d=https%3A%2F%2Favatar-management--avatars.us-west-2.staging.public.atl-paas.net%2Finitials%2FCS-1.png',
		nickname: 'Charlie Sans',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '712020:85713add-d955-447b-84c6-7fce2ab78e0d',
		name: 'Lydia Looi3',
		avatarUrl:
			'https://avatar-management--avatars.us-west-2.staging.public.atl-paas.net/712020:85713add-d955-447b-84c6-7fce2ab78e0d/bbf85236-a0c1-4a5d-86fb-ae0120f07119/128',
		nickname: 'Lydia Looi3',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		locale: 'en-GB',
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '712020:67f884ba-2b63-490b-929a-10987f82a42c',
		name: 'Samantha Tran',
		email: 'stran3@atlassian.com',
		avatarUrl:
			'https://secure.gravatar.com/avatar/faebeb6f8e4213707002681eae3b4778?d=https%3A%2F%2Favatar-management--avatars.us-west-2.staging.public.atl-paas.net%2Finitials%2FST-3.png',
		nickname: 'Samantha Tran',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		locale: 'en-US',
		title: 'Software Engineer',
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '5cee7d47da27f20fbfd5bf8a',
		name: 'Daisy Xu',
		email: 'dxu@atlassian.com',
		avatarUrl:
			'https://secure.gravatar.com/avatar/021ef1edc681fb90fdb3b04471aea952?d=https%3A%2F%2Favatar-management--avatars.us-west-2.staging.public.atl-paas.net%2Finitials%2FDX-1.png',
		nickname: 'Daisy Xu',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		zoneInfo: 'Pacific/Kiritimati',
		locale: 'en-US',
		title: 'Senior Software Engineer',
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '712020:35c060ff-7cf9-4bb6-b939-507004553998',
		name: 'jeff c (User Access Admin)',
		avatarUrl:
			'https://secure.gravatar.com/avatar/b7baa914499a6e7a2cf430d754b5d734?d=https%3A%2F%2Favatar-management--avatars.us-west-2.staging.public.atl-paas.net%2Finitials%2FJA-1.png',
		nickname: 'jeff c (User Access Admin)',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		locale: 'en-GB',
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '712020:d5e2741d-1853-4ba2-a5ab-27bfbebeb118',
		name: 'Mandeep Singh',
		email: 'msingh17@atlassian.com',
		avatarUrl:
			'https://secure.gravatar.com/avatar/e83211b815ae6ee4406272854011fb3b?d=https%3A%2F%2Favatar-management--avatars.us-west-2.staging.public.atl-paas.net%2Finitials%2FMS-1.png',
		nickname: 'Mandeep Singh',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		locale: 'en-GB',
		title: 'Senior Software Engineer',
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '63364c3197148a8301fd462c',
		name: 'Sammy',
		avatarUrl:
			'https://secure.gravatar.com/avatar/af14e4b0ce3e6450395402befcf784ec?d=https%3A%2F%2Favatar-management--avatars.us-west-2.staging.public.atl-paas.net%2Finitials%2FS-1.png',
		nickname: 'Sammy',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		locale: 'en-GB',
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '712020:fb513113-ec8d-47a1-8982-3d2dada0828e',
		name: 'JT.test',
		email: 'jt.test@atlassian.com',
		avatarUrl:
			'https://secure.gravatar.com/avatar/4b00d58e34f345855f2ca983b0baee4f?d=https%3A%2F%2Favatar-management--avatars.us-west-2.staging.public.atl-paas.net%2Finitials%2FJ-6.png',
		nickname: 'JT.test',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '712020:e42e208f-f610-4b23-b0f4-8b69208e151e',
		name: 'Saivardhan Reddy',
		email: 'sreddy11@atlassian.com',
		avatarUrl:
			'https://secure.gravatar.com/avatar/963d22fb082cf0a0f6900a6444575b6a?d=https%3A%2F%2Favatar-management--avatars.us-west-2.staging.public.atl-paas.net%2Finitials%2FSR-3.png',
		nickname: 'Saivardhan Reddy',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		locale: 'en-GB',
		title: 'Software Engineer 2',
		userType: 'DEFAULT',
	},
	{
		entityType: 'USER',
		id: '61f18639632452006b729b35',
		name: 'Cecily Shen',
		email: 'cshen@atlassian.com',
		avatarUrl:
			'https://avatar-management--avatars.us-west-2.staging.public.atl-paas.net/61f18639632452006b729b35/7d4cf6c4-d3c7-4b7c-a378-fbde198413f9/128',
		nickname: 'Cecily Shen',
		matchPositions: {},
		accessLevel: 'CONTAINER',
		accountStatus: 'ACTIVE',
		notMentionable: false,
		zoneInfo: 'Australia/Melbourne',
		locale: 'en-GB',
		location: 'Melbourne',
		title: 'Software Engineer',
		userType: 'DEFAULT',
	},
];

export const SuggestedTeamMembers = ({
	onSuggestedUserClick,
	currentPickedUsers,
	initialValues = [],
}: SuggestedTeamMembersProps) => {
	// const { data: suggestedUsers = , isLoading, hasError, error } = useSuggestedUsersQuery();
	const suggestedUsers = mockData;
	let isLoading = false;

	if (isLoading) {
		return (
			<Flex justifyContent="center" alignItems="center" xcss={styles.container}>
				<Spinner size="small" />
			</Flex>
		);
	}

	// Filter out both current picked users and initial values
	const filteredSuggestedUsers = suggestedUsers
		.filter(
			(user) =>
				!currentPickedUsers.some((pickedUser) => pickedUser.id === user.id) &&
				!initialValues.some((initialUser) => initialUser.id === user.id),
		)
		.slice(0, MAX_SUGGESTED_USERS);

	if (!filteredSuggestedUsers.length) {
		return null;
	}

	return (
		<Flex
			wrap="nowrap"
			justifyContent="start"
			alignItems="center"
			direction="row"
			xcss={styles.container}
		>
			{filteredSuggestedUsers.map((user) => (
				<SuggestedMemberPill
					user={{
						id: user.id,
						name: user.name,
						publicName: user.nickname,
						avatarUrl: user.avatarUrl,
						type: 'user',
						email: user.email || '',
					}}
					onClick={onSuggestedUserClick}
					key={user.id}
				/>
			))}
		</Flex>
	);
};
